import './App.css';
import { useCallback, useEffect, useState } from 'react';
import { getMlsAuthToken, openLogin, getLocale } from './utils.js';

const getMlsApiBase = () =>
  (process.env.REACT_APP_MLS_API_URL || 'http://127.0.0.1:3600').replace(/\/$/, '');

/** @returns {Promise<'allow' | 'block'>} */
async function fetchPreLoginGate() {
  const token = getMlsAuthToken();
  const url = `${getMlsApiBase()}/syndicate/pre-login-check`;
  if( !token ) {
    return 'block';
  }
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status !== 200) {
    return 'block';
  }

  const json = await res.json().catch(() => ({}));
  const isLoggedIn = json?.data?.isLoggedIn === true;
  if (isLoggedIn) {
    return 'block';
  }
  return 'allow';
}

function App() {
  const locale = getLocale() ??
    ((navigator.language || navigator.userLanguage || "en").startsWith("ar")
      ? "ar"
      : "en");
  const [gate, setGate] = useState('checking');
  const [showFallback, setShowFallback] = useState(false);
  const labels = {
    ar: {
      checking: "جارٍ التحقق من الصلاحيات…",
      loadingSso: "جارٍ تحميل تسجيل الدخول في OpenSooq",
      fallback: "اضغط هنا إذا لم يتم تحويلك",
      blockedTitle: "لا يمكن فتح تسجيل الدخول",
      blockedHint:
        "قد تكون متصلاً بالفعل، أو لا تملك صلاحية، أو انتهت الجلسة. ارجع إلى التطبيق وحاول مرة أخرى.",
    },
    en: {
      checking: "Checking permissions…",
      loadingSso: "OpenSooq login is loading",
      fallback: "Click if not redirected",
      blockedTitle: "OpenSooq login can’t open here",
      blockedHint:
        "You may already be connected, not have access, or your session may have expired. Go back to the app and try again.",
    },
  };
  const text = labels[locale] || labels.en;

  const login = useCallback(async () => {
    try {
      await openLogin(locale);
    } catch (error) {
      console.warn("ZZZ - 1 - SSO login failed", error);
    }
  }, [locale]);

  useEffect(() => {
    (async () => {
      try {
        const next = await fetchPreLoginGate();
        setGate(next);
      } catch (e) {
        console.warn("pre-login-check failed", e);
        setGate('block');
      }
    })();
  }, []);

  useEffect(() => {
    if (gate !== 'allow') {
      return undefined;
    }
    const timer2 = setTimeout(() => {
      setShowFallback(true);
    }, 1150);
    const timer1 = setTimeout(() => {
      login();
    }, 150);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [gate, login]);

  if (gate === 'checking') {
    return (
      <div className="App">
        <div id="sso"></div>
        <div className="sso-container">
          <h1 className="sso-title">{text.checking}</h1>
          <div className="sso-spinner" aria-label="Loading" />
        </div>
      </div>
    );
  }

  if (gate === 'block') {
    return (
      <div className="App">
        <div id="sso"></div>
        <div className="sso-container" role="status">
          <h1 className="sso-title">{text.blockedTitle}</h1>
          <p className="sso-blocked-hint">{text.blockedHint}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div id="sso"></div>
      <div className="sso-container">
        <h1 className="sso-title">{text.loadingSso}</h1>
        <div className="sso-spinner" aria-label="Loading" />
        {showFallback ? (
          <button className="sso-fallback" onClick={login} type="button">
            {text.fallback}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default App;
